import {
  type ISODate,
  type Locale,
  diffDays,
  formatMoney,
  formatMoneyCompact,
  parseISODate,
  todayISO,
} from '@esportsbudget/shared';
import { useTranslation } from 'react-i18next';

import { currentIntlLocale } from '@/i18n';

/**
 * Форматирование чисел, денег и дат по текущей локали.
 * Все компоненты используют только эти хелперы — никакого ручного toFixed.
 */

export function useLocale(): Locale {
  const { i18n } = useTranslation();
  return (i18n.language === 'en' ? 'en' : 'ru') as Locale;
}

export function useFormatters() {
  const { t, i18n } = useTranslation('common');
  const locale: Locale = i18n.language === 'en' ? 'en' : 'ru';
  const intlLocale = currentIntlLocale();

  return {
    locale,
    intlLocale,

    money: (minor: number, currency: string, options?: Parameters<typeof formatMoney>[3]) =>
      formatMoney(minor, currency, locale, options),

    moneyCompact: (minor: number, currency: string) => formatMoneyCompact(minor, currency, locale),

    /** Число без валюты. */
    number: (value: number, maximumFractionDigits = 0) =>
      new Intl.NumberFormat(intlLocale, { maximumFractionDigits }).format(value),

    percent: (value: number) =>
      new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 2 }).format(value) + '%',

    /** 28 авг 2026 */
    date: (value: ISODate | null | undefined) => {
      if (!value) return '—';
      return new Intl.DateTimeFormat(intlLocale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(parseISODate(value.slice(0, 10)));
    },

    /** 28 августа 2026 */
    dateLong: (value: ISODate | null | undefined) => {
      if (!value) return '—';
      return new Intl.DateTimeFormat(intlLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(parseISODate(value.slice(0, 10)));
    },

    /** 28.08 */
    dateShort: (value: ISODate | null | undefined) => {
      if (!value) return '—';
      return new Intl.DateTimeFormat(intlLocale, {
        day: '2-digit',
        month: '2-digit',
        timeZone: 'UTC',
      }).format(parseISODate(value.slice(0, 10)));
    },

    /** Август 2026 */
    monthYear: (monthKey: string) => {
      const [year, month] = monthKey.split('-').map(Number);
      const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1));
      const text = new Intl.DateTimeFormat(intlLocale, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(date);
      return text.charAt(0).toUpperCase() + text.slice(1);
    },

    /** Время сообщения: 14:32 или 27 авг, 14:32 если не сегодня. */
    dateTime: (value: string | Date | null | undefined) => {
      if (!value) return '—';
      const date = typeof value === 'string' ? new Date(value) : value;
      const isToday = date.toDateString() === new Date().toDateString();
      return new Intl.DateTimeFormat(intlLocale, {
        ...(isToday ? {} : { day: 'numeric', month: 'short' }),
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },

    /**
     * Полные дата и время в поясе читателя: «7 октября 2026 г., 21:37».
     *
     * Отдельно от `dateTime`: тот прячет дату у сегодняшних записей и не
     * показывает год — для ленты сообщений это удобно, а для срока, до
     * которого нужно ждать месяц, недопустимо. Момент приходит с сервера
     * готовым, здесь он только форматируется: считать его на клиенте значило
     * бы показывать разное на часах, идущих по-разному.
     */
    fullDateTime: (value: string | Date | null | undefined) => {
      if (!value) return '—';
      const date = typeof value === 'string' ? new Date(value) : value;
      if (Number.isNaN(date.getTime())) return '—';

      return new Intl.DateTimeFormat(intlLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    },

    /** «через 3 дня», «сегодня», «5 дней назад» — для сроков выплат. */
    relativeDate: (value: ISODate | null | undefined) => {
      if (!value) return '—';
      const days = diffDays(todayISO(), value.slice(0, 10));
      if (days === 0) return t('today');
      if (days === 1) return t('tomorrow');
      if (days === -1) return t('yesterday');
      if (days > 0) return t('inDays', { count: days });
      return t('daysAgo', { count: Math.abs(days) });
    },

    /** Число дней до даты: отрицательное — просрочка. */
    daysUntil: (value: ISODate) => diffDays(todayISO(), value.slice(0, 10)),

    /**
     * Размер файла с единицей на языке интерфейса.
     * Раньше «КБ» и «МБ» лежали строками прямо в разметке — в английской
     * версии они так и оставались кириллицей.
     */
    fileSize: (bytes: number) => {
      if (bytes < 1024) return `${bytes}\u00A0${t('unit.b')}`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)}\u00A0${t('unit.kb')}`;
      return `${(bytes / 1024 / 1024).toFixed(1)}\u00A0${t('unit.mb')}`;
    },

    /** Готовое число мегабайт — например, лимит тарифа. */
    megabytes: (value: number) => `${value}\u00A0${t('unit.mb')}`,
  };
}

export type Formatters = ReturnType<typeof useFormatters>;
